import { Flex, FlexProps, StackProps, VStack } from '@chakra-ui/react'
import { PhoneNumber, PhoneType } from 'api-user-client'
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber'
import { omit } from 'ramda'
import React, { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'

import {
  DropdownOption,
  Input,
  InputLabel,
  StandardDropdown,
  StandardText,
} from '~shared-components/components'
import { useTranslations } from '~shared-components/hooks/useTranslations'
import { TranslationKey } from '~shared-components/translations/default'

import { AddPhoneButton } from './AddPhoneButton'
import { RemovePhoneButton } from './RemovePhoneButton'

export const assignIdsToPhoneNumbers = (
  phoneNumbers: PhoneNumber[] | undefined,
): PhoneNumberWithId[] | undefined =>
  phoneNumbers?.map(phoneNumber => ({ ...phoneNumber, id: uuidv4() }))

export const removeIdsFromPhoneNumbers = (
  phoneNumbers: PhoneNumberWithId[] | PhoneNumber[] | undefined,
): PhoneNumber[] | undefined => phoneNumbers?.map(omit(['id']))

const PhoneNumberTypeContainer: React.FC<FlexProps> = props => (
  <Flex flex={1} flexDirection="column" {...props} />
)

const PhoneNumberInputContainer: React.FC<FlexProps> = props => (
  <Flex flex={2} flexDirection="column" {...props} />
)

const PhoneNumbersContainer: React.FC<StackProps> = props => (
  <VStack w="100%" {...props} />
)

interface PhoneNumberDefaultProps {
  maxPhoneNumbers?: number
  readOnly?: boolean
}
export interface PhoneNumberWithId extends PhoneNumber {
  id: string
}

export interface PhoneNumberFormProps extends PhoneNumberDefaultProps {
  direction?: 'row' | 'column'
  disabled?: boolean
  onPhoneNumbersChanged: (phoneNumbers: PhoneNumberWithId[]) => void
  phoneNumbers: PhoneNumberWithId[] | undefined
}

interface PhoneNumberInputRowProps extends PhoneNumberDefaultProps {
  addPhoneNumber: () => void
  canAddPhoneNumber: boolean
  direction: 'row' | 'column'
  id: string | number
  onChangePhoneNumber: (phoneNumer: PhoneNumberWithId) => void
  phoneNumber: PhoneNumberWithId
  removePhoneNumber: (phoneNumber: PhoneNumberWithId) => void
  validate: (value: string) => string | undefined
}

const phoneNumberUtil = PhoneNumberUtil.getInstance()

const PhoneNumberInputRow = ({
  addPhoneNumber,
  canAddPhoneNumber,
  direction,
  id,
  onChangePhoneNumber,
  phoneNumber,
  readOnly,
  removePhoneNumber,
  validate,
}: PhoneNumberInputRowProps) => {
  const t = useTranslations()
  const { formState, register, unregister } = useFormContext()
  const fieldName = useMemo(
    () =>
      phoneNumber.isPrimary ? 'phone_number' : `secondary_phone_number_${id}`,
    [phoneNumber.isPrimary, id],
  )
  const { errors } = formState
  const errorMessage = errors[fieldName] && errors[fieldName].message

  const isValidPhoneNumber = (str: string) => {
    try {
      if (!str.match(/^\+(\d|\s)+$/)) return false
      const trimmed = str.replace(/\s/g, '')
      return phoneNumberUtil.isValidNumber(phoneNumberUtil.parse(trimmed))
    } catch {
      return false
    }
  }

  const phoneTypeOptions: DropdownOption[] = [
    {
      description: t('label.Mobile'),
      value: PhoneType.Mobile,
    },
    {
      description: t('label.Landline'),
      value: PhoneType.Landline,
    },
  ]

  useEffect(() => {
    // Unregister field from react-hook-form on unmount
    return () => {
      unregister(fieldName)
    }
  }, [unregister, fieldName])

  return (
    <Flex mb={4} w="100%">
      <Flex alignItems="flex-start" justifyContent="flex-start" minW={7}>
        {!readOnly && (
          <>
            {phoneNumber.isPrimary ? (
              <AddPhoneButton
                disabled={!canAddPhoneNumber}
                onPhoneNumberAdded={addPhoneNumber}
              />
            ) : (
              <RemovePhoneButton
                onPhoneNumberRemoved={removePhoneNumber}
                phoneNumber={phoneNumber}
              />
            )}
          </>
        )}
      </Flex>
      <Flex direction={direction} w="100%">
        <PhoneNumberTypeContainer
          mb={direction === 'column' ? 2 : 0}
          mr={direction === 'row' ? 4 : 0}
        >
          <InputLabel>{t('label.type')}</InputLabel>
          {readOnly ? (
            <StandardText>
              {t(`label.${phoneNumber.type}` as TranslationKey)}
            </StandardText>
          ) : (
            <StandardDropdown
              onOptionSelect={value =>
                onChangePhoneNumber({
                  ...phoneNumber,
                  type: value as PhoneType,
                })
              }
              options={phoneTypeOptions}
              value={phoneNumber.type}
            />
          )}
        </PhoneNumberTypeContainer>
        <PhoneNumberInputContainer>
          <InputLabel required={!readOnly && phoneNumber.isPrimary}>
            {t('label.phonenumber')}
          </InputLabel>
          {readOnly ? (
            <StandardText>{phoneNumber.number}</StandardText>
          ) : (
            <Input
              {...register(fieldName, {
                shouldUnregister: true,
                validate: {
                  nonEmpty: value =>
                    (value && value.trim().length > 0) ||
                    t('field.required').replace(
                      '@@VALUE@@',
                      t('label.phonenumber'),
                    ),
                  validFormat: value =>
                    isValidPhoneNumber(value) || t('field.invalidphonenumber'),

                  validateExternal: validate,
                },
              })}
              defaultValue={phoneNumber.number}
              error={Boolean(errorMessage)}
              helper={errorMessage}
              name={fieldName}
              onValueChange={value =>
                onChangePhoneNumber({
                  ...phoneNumber,
                  number: isValidPhoneNumber(value)
                    ? phoneNumberUtil.format(
                        phoneNumberUtil.parse(value),
                        PhoneNumberFormat.E164,
                      )
                    : value,
                })
              }
              placeholder="+61212345678"
              type="tel"
            />
          )}
        </PhoneNumberInputContainer>
      </Flex>
    </Flex>
  )
}

export const PhoneNumbers = ({
  direction = 'row',
  disabled,
  maxPhoneNumbers,
  onPhoneNumbersChanged,
  phoneNumbers = [],
  ...props
}: PhoneNumberFormProps) => {
  const t = useTranslations()

  const onChangePhoneNumber = (newPhoneNumber: PhoneNumberWithId) => {
    const phoneNumberToAdjust = phoneNumbers.find(
      ({ id }) => newPhoneNumber.id === id,
    )

    if (phoneNumberToAdjust) {
      phoneNumberToAdjust.number = newPhoneNumber.number
      phoneNumberToAdjust.type = newPhoneNumber.type

      onPhoneNumbersChanged(phoneNumbers)
    }
  }

  const addPhoneNumber = () => {
    onPhoneNumbersChanged([
      ...phoneNumbers,
      {
        id: uuidv4(),
        isPrimary: false,
        number: '',
        type: PhoneType.Mobile,
      },
    ])
  }

  const removePhoneNumber = (phoneNumberToRemove: PhoneNumberWithId) => {
    onPhoneNumbersChanged([
      ...phoneNumbers.filter(({ id }) => id !== phoneNumberToRemove.id),
    ])
  }

  const validateUniq = (value: string) =>
    phoneNumbers.filter(nr => nr.number === value).length > 1
      ? t('field.duplicatedphonenumber')
      : undefined
  return (
    <PhoneNumbersContainer>
      {phoneNumbers.map((phoneNumber, index) => (
        <PhoneNumberInputRow
          key={`${index}-${phoneNumber.id}`}
          addPhoneNumber={addPhoneNumber}
          canAddPhoneNumber={
            !maxPhoneNumbers || phoneNumbers.length < maxPhoneNumbers
          }
          direction={direction}
          id={`id${index}`}
          onChangePhoneNumber={onChangePhoneNumber}
          phoneNumber={phoneNumber}
          readOnly={disabled}
          removePhoneNumber={removePhoneNumber}
          validate={validateUniq}
          {...props}
        />
      ))}
    </PhoneNumbersContainer>
  )
}
